import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PhoneContactDto } from '../models/phone-contact.dto';
import { ToastService } from '../toast/toast.service';
import { SmsMessageDto } from '../models/sms-message.dto';
import { SmsService } from '../services/sms.service';
import { SmsMessageListDto } from '../models/sms-message-list.dto';
import { BeneficiaryPhoneContactDto } from '../models/beneficiary-phone-contact.dto';
import { MailingConfirmationComponent } from '../mailing confirmation/mailing-confirmation.component';

@Component({
  selector: 'app-sms-composer',
  templateUrl: './sms-composer.component.html',
})
export class SmsComposerComponent implements OnInit {
  public form: UntypedFormGroup;
  public submitted: boolean = false;
  public readonly toMessageMaxlength: number = 160;
  private multiple: boolean = false;

  private beneficiaryIds: number[] = [];
  private beneficiaryId: number;
  private beneficiaryName: string;
  private to: string | PhoneContactDto;
  private content: string = '';

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly smsService: SmsService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
  ) {}

  public ngOnInit(): void {
    this.loadForm();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      to: [this.to, Validators.required],
      content: [this.content, [Validators.required, Validators.maxLength(this.toMessageMaxlength)]],
    });

    this.form.controls.to.disable();
  }

  public onInit(beneficiaryId: number, beneficiaryName: string, to: string | PhoneContactDto, content: string = ''): void {
    this.beneficiaryId = beneficiaryId;
    this.beneficiaryName = beneficiaryName;
    this.to = to;
    this.content = content;
  }

  public onInitList(beneficiaryIds: number[], content: string = ''): void {
    this.multiple = true;
    this.beneficiaryIds = beneficiaryIds;
    this.to = this.beneficiaryIds.length + ' ' + (this.beneficiaryIds.length === 1 ? 'bénéficiaire' : 'bénéficiaires');
    this.content = content;
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.form.valid) {
      return this.multiple ? this.onSubmitForMultiple() : this.onSubmitForSingle();
    }
  }

  private async onSubmitForSingle(): Promise<void> {
    const message: SmsMessageDto = {
      beneficiaryId: this.beneficiaryId,
      beneficiaryName: this.beneficiaryName,
      to: this.form.controls.to.value,
      content: this.form.controls.content.value,
    };
    await this.smsService.sendSms(message);
    this.toastService.show('Votre message SMS a été envoyé avec succès.', 'success');
    this.cancel();
  }

  private async onSubmitForMultiple(): Promise<void> {
    const message: SmsMessageListDto = {
      beneficiaryIds: this.beneficiaryIds,
      content: this.form.controls.content.value,
    };
    const result: BeneficiaryPhoneContactDto[] = await this.smsService.sendSmsList(message);
    this.cancel();

    if (result?.length) {
      await this.openAlertModal(result.map((item: BeneficiaryPhoneContactDto) => item.name));
    } else {
      this.toastService.show('Votre message SMS a été envoyé avec succès.', 'success');
    }
  }

  private async openAlertModal(beneficiaryNames: string[]): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(MailingConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: MailingConfirmationComponent = modal.componentInstance as MailingConfirmationComponent;
    return modalComponent.confirm("Le message SMS n'a pas été envoyé aux bénéficiaires suivants: ", beneficiaryNames);
  }

  public cancel(): void {
    this.modal.close();
  }

  public isPhoneContact(value: any): value is PhoneContactDto {
    return value instanceof PhoneContactDto;
  }

  public isString(value: any): value is string {
    return typeof value === 'string';
  }
}
